import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCardContext } from '../contexts/CardContext';
import PinInput from '../components/PinInput';
import ActivationLoading from '../components/ActivationLoading';
import { ApiService } from '../services/apiService';
import { CardStateService } from '../services/cardStateService';
import { CardState } from '../types/cardTypes';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { getQuote, getAnyQuote, clearQuote } from '../utils/quoteStorage';
import { AppHeader } from '../components/AppHeader';
// @mcc-dev-preview:import
import { parseDevState } from '../dev/devPreview';
// @mcc-dev-preview:import
import { DEV_CARD_ID } from '../dev/devPreviewMocks';
import './CardActivation.css';

// @mcc-dev-preview:start dev-preview-types
type CardActivationDevState = 'validating' | 'ready' | 'error' | 'dialog' | 'activating';

const CARD_ACTIVATION_DEV_STATES: CardActivationDevState[] = [
  'validating',
  'ready',
  'error',
  'dialog',
  'activating',
];
// @mcc-dev-preview:end dev-preview-types

const CardActivation: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cardData, cardId: contextCardId } = useCardContext();
  const devState = parseDevState(searchParams, CARD_ACTIVATION_DEV_STATES);
  
  const finalCardId = contextCardId || searchParams.get('cardId');
  
  const [pin, setPin] = useState('');
  const [verifyPin, setVerifyPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isActivating, setIsLoading] = useState(false);
  const [activatingCardId, setActivatingCardId] = useState<string>('');
  const [isValidatingState, setIsValidatingState] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [showDifferentBrowserMessage, setShowDifferentBrowserMessage] = useState(false);

  // @mcc-dev-preview:start dev-preview-seed
  useEffect(() => {
    if (!devState) return;

    setActivatingCardId(searchParams.get('cardId') || DEV_CARD_ID);

    if (devState === 'validating') {
      return;
    }

    setIsValidatingState(false);

    switch (devState) {
      case 'error':
        setError(
          searchParams.get('errorMessage') ??
            'Card is not ready for activation. Current state: Invalid'
        );
        break;
      case 'dialog':
        setOpenDialog(true);
        break;
      case 'activating':
        setIsLoading(true);
        break;
      case 'ready':
      default:
        break;
    }
  }, [devState, searchParams]);
  // @mcc-dev-preview:end dev-preview-seed

  // Validate card state on page load
  useEffect(() => {
    if (devState) return;

    const validateCardState = async () => {
      try {
        const cardId = finalCardId;
        
        if (!cardId) {
          const errorMessage = 'Card ID is required';
          navigate(CardStateService.getErrorRoute(errorMessage));
          return;
        }

        const cardData = await ApiService.getCardByCardId(cardId);
        
        if (!cardData) {
          const errorMessage = `Card not found for ID: ${cardId}`;
          navigate(CardStateService.getErrorRoute(errorMessage, cardId));
          return;
        }
        
        const kyc = await ApiService.getSumsubKycStatus();
        const kycReady = kyc.success && kyc.data?.readyForActivation;

        if (!kycReady) {
          navigate(`/kyc-verification?cardId=${encodeURIComponent(cardId)}`);
          return;
        }

        // Card state can lag behind Sumsub approval (e.g. still EmailVerified or KYCPENDING).
        const activationAllowedStates = new Set([
          CardState.READY_FOR_ACTIVATION,
          CardState.OWNED,
          CardState.KYCPENDING,
          CardState.EMAIL_VERIFIED,
        ]);

        // #region agent log
        fetch('http://127.0.0.1:7361/ingest/ac35d597-8d02-4005-931f-a2b834303e25',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'55344c'},body:JSON.stringify({sessionId:'55344c',location:'CardActivation.tsx:validate',message:'Card activation validation',data:{cardId,cardState:cardData.state,kycReady,allowed:activationAllowedStates.has(cardData.state),runId:'post-fix-2'},timestamp:Date.now(),hypothesisId:'F'})}).catch(()=>{});
        // #endregion

        if (!activationAllowedStates.has(cardData.state)) {
          const errorMessage = `Card is not ready for activation. Current state: ${cardData.state}`;
          navigate(CardStateService.getErrorRoute(errorMessage, cardId));
          return;
        }

        setIsValidatingState(false);
      } catch (error: any) {
        console.error('❌ CardActivation: Error validating card state:', error);
        const errorMessage = 'Failed to verify card state';
        navigate(CardStateService.getErrorRoute(errorMessage, finalCardId || undefined));
      }
    };

    validateCardState();
  }, [finalCardId, navigate, devState]);

  const handlePinChange = (value: string) => {
    setPin(value);
  };

  const handleVerifyPinChange = (value: string) => {
    setVerifyPin(value);
  };

  const handleSubmit = () => {
    // Validate PIN length (must be 6 digits)
    if (pin.length !== 6 || verifyPin.length !== 6) {
      alert('Please enter a complete 6-digit PIN in both fields');
      return;
    }
    
    // Validate PINs match
    if (pin !== verifyPin) {
      alert('PINs do not match. Please try again.');
      return;
    }

    // Show confirmation dialog
    setOpenDialog(true);
  };

  const createBanxaOrderFromQuote = async (cardId: string, quote: any): Promise<boolean> => {
    try {
      // Validate quote data
      if (!quote || !quote.paymentMethodId || !quote.fiatCode || !quote.coinCode) {
        console.error('❌ [Card Activation] Invalid quote data:', quote);
        throw new Error('Invalid quote data: missing required fields');
      }

      const orderRequest = {
        cardId,
        fiatAmount: quote.fiatAmount,
        cryptoAmount: quote.cryptoAmount,
        fiatCode: quote.fiatCode,
        coinCode: quote.coinCode,
        paymentMethodId: quote.paymentMethodId,
        processingFee: quote.processingFee || 0,
        networkFee: quote.networkFee || 0,
        blockchain: quote.blockchain
      };

      const orderResponse = await ApiService.createQuoteOrder(orderRequest);

      // Verify response structure
      if (!orderResponse) {
        console.error('❌ [Card Activation] No response from API');
        throw new Error('No response from Banxa order creation API');
      }

      if (orderResponse.success && orderResponse.data?.checkoutUrl) {
        const checkoutUrl = orderResponse.data.checkoutUrl;
        
        // Validate checkout URL
        if (!checkoutUrl || typeof checkoutUrl !== 'string' || !checkoutUrl.startsWith('http')) {
          console.error('❌ [Card Activation] Invalid checkout URL:', checkoutUrl);
          throw new Error('Invalid checkout URL received from API');
        }

        clearQuote(cardId);
        window.location.href = checkoutUrl;
        // Return true to indicate we're redirecting (busy wait should stay visible)
        return true;
      } else {
        const errorMsg = orderResponse.error || 'Unknown error';
        console.error('❌ [Card Activation] Failed to create Banxa order');
        console.error('❌ [Card Activation] Error details:', errorMsg);
        console.error('❌ [Card Activation] Full response:', orderResponse);
        
        setIsLoading(false); // Reset loading state on error
        navigate(`/purchase-crypto?cardId=${cardId}`);
        return false;
      }
    } catch (error) {
      console.error('❌ [Card Activation] Error creating Banxa order:', error);
      console.error('❌ [Card Activation] Error details:', error instanceof Error ? error.message : String(error));
      console.error('❌ [Card Activation] Error stack:', error instanceof Error ? error.stack : 'No stack trace');
      
      setIsLoading(false); // Reset loading state on error
      navigate(`/purchase-crypto?cardId=${cardId}`);
      return false;
    }
  };

  const handleContinue = async () => {
    if (devState) {
      setOpenDialog(false);
      return;
    }

    setOpenDialog(false);
    const cardIdToActivate = finalCardId ?? '';
    setActivatingCardId(cardIdToActivate);
    setIsLoading(true);

    try {
      // Simulate a delay to show the activation process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const response = await ApiService.activateCard(finalCardId || '', pin);
      
      // Check if the API call was successful
      if (response.success) {
        let quote: any = null;
        let quoteCardId = finalCardId || '';
        
        if (finalCardId) {
          quote = getQuote(finalCardId);
        }

        if (!quote) {
          const anyQuoteResult = getAnyQuote();
          if (anyQuoteResult) {
            quote = anyQuoteResult.quote;
            quoteCardId = anyQuoteResult.cardId;
          }
        }

        if (quote && quoteCardId) {
          const isRedirecting = await createBanxaOrderFromQuote(quoteCardId, quote);
          // If redirecting, don't reset isActivating - let the redirect happen while busy wait is visible
          if (!isRedirecting) {
            // Only reset if not redirecting (error case)
            setIsLoading(false);
          }
          // If isRedirecting is true, keep isActivating true and let the redirect happen
        } else {
          setIsLoading(false);
          setShowDifferentBrowserMessage(true);
          setTimeout(() => {
            setShowDifferentBrowserMessage(false);
            navigate(`/purchase-crypto?cardId=${finalCardId || ''}`);
          }, 3000);
        }
      } else {
        // Redirect to error page on failure
        setIsLoading(false); // Reset loading state on activation failure
        const errorMessage = response.message || 'Failed to activate card';
        navigate(CardStateService.getErrorRoute(errorMessage, finalCardId || undefined));
      }
    } catch (error) {
      console.error('Error activating card:', error);
      // Redirect to error page on exception
      setIsLoading(false); // Reset loading state on error
      const errorMessage = 'Failed to activate card';
      navigate(CardStateService.getErrorRoute(errorMessage, finalCardId || undefined));
    }
  };

  const handleGoBack = () => {
    setOpenDialog(false);
  };

  const handleBack = () => {
    navigate(-1);
  };

      // Show loading state while validating card state
  if (isValidatingState) {
    return (
      <div className="card-activation-container ev-page ev-page-enter">
        <AppHeader />
        <main className="card-activation-main">
          <div className="content-wrapper">
            <div className="card-activation-loading-spinner">
              <div className="card-activation-spinner"></div>
              <p>Validating card state...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show activation loading state
  if (isActivating) {
    return (
      <div className="card-activation-container ev-page ev-page-enter">
        <AppHeader />
        <main className="card-activation-main">
          <div className="content-wrapper">
            <ActivationLoading
              cardId={activatingCardId || (finalCardId ?? '')}
              statusLabel="Activating"
            />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="card-activation-container ev-page ev-page-enter">
      <AppHeader />
      <h1 className="main-heading ev-headline-main" style={{ textAlign: 'center' }}>CARD ACTIVATION</h1>

      {/* Main Content */}
      <main className="card-activation-main">
        <div className="content-wrapper">
          <div className="page-header">
          <h2 className="main-subtitle ev-instruction">Set up your card PIN</h2>
          </div>
          {/* White card: PIN Form (Figma layout) */}
          <div className="card-activation-card">
            <div className="card-activation-form">
              <PinInput
                pin={pin}
                verifyPin={verifyPin}
                onPinChange={handlePinChange}
                onVerifyPinChange={handleVerifyPinChange}
                isLoading={isActivating}
              />

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <div className="card-activation-buttons">
              <button
                  type="button"
                  className="submit-button"
                  onClick={handleSubmit}
                  disabled={pin.length !== 6 || verifyPin.length !== 6}
                >
                  Activate Card
                </button>
                <button
                  type="button"
                  className="back-button"
                  onClick={handleBack}
                >
                  <img src="/back-icon.svg" alt="" className="back-button-icon" aria-hidden="true" />
                  <span>Back</span>
                </button>                
              </div>
            </div>
          </div>
          <div className="page-header">
            <h4 className="card-activation-disclaimer">
            Only you know the PIN, write it down and keep it somewhere safe.
          </h4></div>
        </div>
      </main>

      <Dialog
        open={openDialog}
        onClose={isActivating ? undefined : handleGoBack}
        PaperProps={{
          sx: {
            background: 'rgba(10, 10, 10, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '20px',
            color: '#ffffff'
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', color: '#ffffff' }}>
          Important Notice
        </DialogTitle>
        <DialogContent>
          <div style={{ textAlign: 'center', color: '#ffffff' }}>
            Make sure you write down your pin and keep it somewhere safe. We won't be able to recover it in case the pin is lost.
          </div>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
          <Button
            onClick={handleGoBack}
            variant="outlined"
            disabled={isActivating}
            sx={{
              color: '#ffffff',
              borderColor: 'rgba(255, 255, 255, 0.2)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.4)',
              }
            }}
          >
            Go back
          </Button>
          <Button
            onClick={handleContinue}
            variant="contained"
            disabled={isActivating}
            sx={{
              background: 'var(--ev-btn-primary, #5D2FD2)',
              color: '#ffffff',
              fontWeight: 'bold',
              '&:hover': {
                background: 'var(--ev-btn-primary-hover, #4a26a8)',
              }
            }}
          >
            {isActivating ? 'Activating...' : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>
      {showDifferentBrowserMessage && (
        <div className="welcome-back-popup">
          <div className="welcome-back-content">
            <h2>Different Browser Detected</h2>
            <p>I can see you are using a different browser, let's quickly create the quote again.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CardActivation;
